import { TWO_FACTOR_TYPE } from "@tastyworks/shared-utils/misc/constants"
import { isEmpty, isNil, last, merge, startsWith } from "lodash-es"
import type { RestResource } from "./common"
import type { DeSer, Parser, Serializer, Updater } from "./deser"
import type Logger from "./logger"
import {
  UrlPathBuilder,
  toFormData,
  type FileParams,
  type Params,
} from "./request"
import {
  BaseResponse,
  parseErrorContainer,
  parseItemsResponse,
  updateItemsResponse,
  updateSingleResponse,
  type ErrorContainer,
  type ItemsResponse,
  type SingleResponse,
} from "./response"
import type TwSession from "./session"
import type { SessionErrorHandler } from "./session-error-handler"
import type { ArrayMap } from "./util/collection"
import {
  JsonBuilder,
  JsonHelper,
  type JsonKeyExtractor,
  type JsonMap,
} from "./util/json"

export const JSON_MIME_TYPE = "application/json"
export const MULTIPART_FORM_MIME_TYPE = "multipart/form-data"
export const CONTENT_TYPE = "Content-Type"
export const AUTHORIZATION = "Authorization"
export const OTP_HEADER = "x-tastyworks-otp"
export const RECOVERY_CODE_HEADER = "x-tastyworks-recovery-code"
export const CASTLE_TOKEN_HEADER = "X-Castle-Request-Token"
export const CSRF_HEADER = "X-Tastyworks-CSRF"
// CSRF defense is the mere presence of this custom header: browsers can't set it
// on forged cross-origin requests, so the value itself is irrelevant.
export const CSRF_HEADER_VALUE = "1"
export const DEVICE_CHALLENGE_TOKEN_HEADER = "X-Tastyworks-Challenge-Token"
export const ATTEMPTS_REMAINING_HEADER = "x-tastyworks-login-attempts-remaining"
export const RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER =
  "x-tastyworks-recovery-code-login-attempts-remaining"
export const TWO_FACTOR_METHODS_AVAILABLE_HEADER =
  "x-tastyworks-2fa-methods-available"
export const TWO_FACTOR_METHOD_HEADER = "x-tastyworks-2fa-method"

const RECOVERY_CODE_METHOD = "recovery_code"

/**
 * Whether the response advertises recovery codes as an available second
 * factor via the semicolon-separated 2FA-methods-available header
 * (e.g. "authenticator; recovery_code").
 */
function splitMethodsAvailableHeader(response: Response): string[] {
  const methods = response.headers.get(TWO_FACTOR_METHODS_AVAILABLE_HEADER)
  if (methods === null) {
    return []
  }
  return methods.split(";").map((method) => method.trim())
}

export function hasRecoveryCodeMethodAvailable(response: Response): boolean {
  return splitMethodsAvailableHeader(response).includes(RECOVERY_CODE_METHOD)
}

/**
 * The active two-factor methods advertised by the semicolon-separated
 * 2FA-methods-available header (e.g. "authenticator; sms; recovery_code").
 * Only challenge-capable methods are returned: recovery_code, email and
 * unknown entries are skipped.
 */
export function availableTwoFactorMethods(
  response: Response
): TWO_FACTOR_TYPE[] {
  return splitMethodsAvailableHeader(response)
    .map((method) => parseTwoFactorType(method))
    .filter((type): type is TWO_FACTOR_TYPE => type !== null)
}

/**
 * Returns the appropriate attempts-remaining header based on whether
 * a recovery code is being used for authentication.
 */
export function attemptsRemainingHeaderFor(
  session: Pick<TwSession, "recoveryCode">
): string {
  return session.recoveryCode.length > 0
    ? RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER
    : ATTEMPTS_REMAINING_HEADER
}
const HTTP_STATUS_NO_CONTENT = 204
const HTTP_INVALID_SESSION = 401
const HTTP_FORBIDDEN = 403
const HTTP_TOO_MANY_REQUESTS = 429
const HTTP_INTERNAL_SERVER_ERROR = 500

export type HttpMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT"

export type Transport = "bearer" | "cookie-csrf"

export const INVALID_SESSION_MESSAGE = "Invalid Session"
export const INTERNAL_SERVER_ERROR_MESSAGE = "Internal Server Error"
export const TWO_FACTOR_AUTH_ERROR = "Two Factor Auth Required"
export const HTTP_TOO_MANY_REQUESTS_MESSAGE = "Too Many Requests"
export const INVALID_CREDENTIALS_CODE = "invalid_credentials"
export const INVALID_SECURITY_QUESTION_CODE = "invalid_security_question_answer"
export const OTP_RATE_LIMIT_EXCEEDED_CODE = "otp_rate_limit_exceeded"
const DEVICE_CHALLENGE_ERROR_CODE = "device_challenge_required"

export interface FetchOption {
  body?: FormData | string
}

export interface RequestOptions {
  signal?: AbortSignal
  timeoutMs?: number
}

const resolveSignal = (options?: RequestOptions) => {
  const { signal, timeoutMs } = options ?? {}
  if (timeoutMs === undefined) {
    return signal
  }
  const timeoutSignal = AbortSignal.timeout(timeoutMs)
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal
}

export class InvalidSessionError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    readonly jsonHelper?: JsonHelper
  ) {
    super(INVALID_SESSION_MESSAGE)

    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, InvalidSessionError.prototype)
  }
}

export class TwoFactorAuthError extends Error {
  readonly recoveryCodeAttemptsRemaining: string | null

  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    readonly jsonHelper: JsonHelper,
    readonly attemptsRemaining: string | null
  ) {
    super(TWO_FACTOR_AUTH_ERROR)

    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, TwoFactorAuthError.prototype)

    this.recoveryCodeAttemptsRemaining = response.headers.get(
      RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER
    )
  }

  get twoFactorType(): TWO_FACTOR_TYPE | null {
    return twoFactorType(this.response)
  }

  get availableMethods(): TWO_FACTOR_TYPE[] {
    return availableTwoFactorMethods(this.response)
  }

  get hasFallbackMethodAvailable(): boolean {
    return this.availableMethods.some((method) => method !== this.twoFactorType)
  }

  get hasLockedAccount() {
    return this.attemptsRemaining === null || this.attemptsRemaining === "0"
  }

  get hasRecoveryCodesAvailable(): boolean {
    return hasRecoveryCodeAttemptsRemaining(this.recoveryCodeAttemptsRemaining)
  }

  get hasRecoveryCodeMethodAvailable(): boolean {
    return hasRecoveryCodeMethodAvailable(this.response)
  }
}

export class DeviceChallengeError extends Error {
  constructor(
    readonly code: string,
    readonly message: string,
    readonly deviceChallengeToken: string,
    readonly response: Response
  ) {
    super(message)
  }
}

export class TooManyRequestsError extends Error {
  readonly recoveryCodeAttemptsRemaining: string | null

  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    message?: string
  ) {
    // oxlint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    super(message || HTTP_TOO_MANY_REQUESTS_MESSAGE)
    Object.setPrototypeOf(this, TooManyRequestsError.prototype)

    this.recoveryCodeAttemptsRemaining = response.headers.get(
      RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER
    )
  }

  get twoFactorType(): TWO_FACTOR_TYPE | null {
    return twoFactorType(this.response)
  }

  get availableMethods(): TWO_FACTOR_TYPE[] {
    return availableTwoFactorMethods(this.response)
  }

  get hasRecoveryCodesAvailable(): boolean {
    return hasRecoveryCodeAttemptsRemaining(this.recoveryCodeAttemptsRemaining)
  }
}

// The header is only sent when the user holds unused recovery codes;
// "0" means they have codes but are recovery-code locked out.
function hasRecoveryCodeAttemptsRemaining(
  attemptsRemaining: string | null
): boolean {
  return attemptsRemaining !== null && attemptsRemaining !== "0"
}

export class InternalServerError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response
  ) {
    super(INTERNAL_SERVER_ERROR_MESSAGE)

    // NOTE [DK]:
    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, InternalServerError.prototype)
  }
}

export class InvalidAnswerError extends Error {
  constructor(
    readonly message: string,
    readonly attemptsRemaining: string | null
  ) {
    super(message)
  }

  get hasLockedAccount() {
    return this.attemptsRemaining === null || this.attemptsRemaining === "0"
  }
}

function isOTPStatusCode(status: number): boolean {
  return status === HTTP_INVALID_SESSION || status === HTTP_FORBIDDEN
}

export function isOTPResponse(response: Response): boolean {
  const header = response.headers.get(OTP_HEADER)
  return (
    isOTPStatusCode(response.status) &&
    !isNil(header) &&
    startsWith(header, "required")
  )
}

export function isInvalidCredentialsResponse(error: ErrorContainer) {
  return error.code === INVALID_CREDENTIALS_CODE
}

export function isInvalidSecurityQuestionResponse(
  error: ErrorContainer
): boolean {
  return error.code === INVALID_SECURITY_QUESTION_CODE
}

export function isInvalidAnswerError(
  error: unknown
): error is InvalidAnswerError {
  return error instanceof InvalidAnswerError
}

export function isDeviceChallengeResponse(
  response: Response,
  errorCode: string
) {
  return (
    response.status === HTTP_FORBIDDEN &&
    errorCode === DEVICE_CHALLENGE_ERROR_CODE
  )
}

export function isDeviceChallengeError(
  error: unknown
): error is DeviceChallengeError {
  return (
    error instanceof DeviceChallengeError &&
    isDeviceChallengeResponse(error.response, error.code)
  )
}

export function isOTPError(error: unknown): error is TwoFactorAuthError {
  return error instanceof TwoFactorAuthError && isOTPResponse(error.response)
}

export function isTooManyRequestsResponse(response: Response): boolean {
  return response.status === HTTP_TOO_MANY_REQUESTS
}

export function isTooManyRequestsError(
  error: unknown
): error is TooManyRequestsError {
  return error instanceof TooManyRequestsError
}

export function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  )
}

export function twoFactorType(response: Response): TWO_FACTOR_TYPE | null {
  const otpHeader = response.headers.get(OTP_HEADER)
  if (isNil(otpHeader)) {
    return null
  }

  const twoFactorMethod = last(otpHeader.split(";"))
  if (isNil(twoFactorMethod)) {
    return null
  }

  return parseTwoFactorType(twoFactorMethod)
}

function parseTwoFactorType(value: string): TWO_FACTOR_TYPE | null {
  switch (value.trim()) {
    case TWO_FACTOR_TYPE.AUTHENTICATOR:
      return TWO_FACTOR_TYPE.AUTHENTICATOR
    case TWO_FACTOR_TYPE.SMS:
      return TWO_FACTOR_TYPE.SMS
    default:
      return null
  }
}

export async function toJsonHelper(response: Response) {
  if (response.status === HTTP_STATUS_NO_CONTENT) {
    // 204 responses don't have a JSON body so passing through an empty object
    return new JsonHelper({})
  }

  const contentType = response.headers.get(CONTENT_TYPE)

  if (!contentType?.startsWith(JSON_MIME_TYPE)) {
    return new JsonHelper({})
  }

  const json = (await response.json()) as JsonMap
  return new JsonHelper(json)
}

type HttpAction = (
  path: string,
  body: any,
  params?: Params
) => Promise<JsonHelper>

const actionOnSingleResource = async <T extends RestResource>(
  httpAction: HttpAction,
  target: T,
  deSer: DeSer<T>
): Promise<SingleResponse<T>> => {
  const builder = new JsonBuilder()

  deSer.serialize(target, builder)

  const resourceParams = {
    id: target.id,
  }

  const updater = (_target: T, helper: JsonHelper) => {
    deSer.update(_target, helper)
  }

  return httpAction(":id", builder.json, resourceParams).then((helper) =>
    updateSingleResponse(helper, target, updater)
  )
}

export default class HttpClient {
  public readonly baseUrl: UrlPathBuilder
  public readonly session: TwSession
  public readonly defaultParams: object

  public constructor(
    private readonly logger: Logger,
    readonly fetcher: typeof fetch,
    $baseUrl: string,
    $twSession: TwSession,
    readonly sessionErrorHandler: SessionErrorHandler,
    $urlPathBuilder?: UrlPathBuilder,
    defaultParams = {},
    readonly transport: Transport | (() => Transport) = "bearer"
  ) {
    this.defaultParams = defaultParams
    this.baseUrl = $urlPathBuilder
      ? $urlPathBuilder.nested($baseUrl, this.defaultParams)
      : new UrlPathBuilder($baseUrl, "", this.defaultParams)
    this.session = $twSession
  }

  get currentTransport(): Transport {
    return typeof this.transport === "function"
      ? this.transport()
      : this.transport
  }

  nested = (path: string, defaultParams = {}): HttpClient =>
    new HttpClient(
      this.logger,
      this.fetcher,
      path,
      this.session,
      this.sessionErrorHandler,
      this.baseUrl,
      merge(defaultParams, this.defaultParams),
      this.transport
    )

  private readonly fetchWithErrorHandler = async (
    url: string,
    request: RequestInit
  ) => {
    try {
      /* cors is the default mode for browsers,
      but since svelte-kit may run in a node environment,
      we need to set it explicitly in order to support that framework */
      request.mode = "cors"
      return await this.fetcher(url, request)
    } catch (error) {
      if (!isAbortError(error)) {
        this.sessionErrorHandler.onHttpNetworkError(url, request, error)
      }
      throw error
    }
  }

  fetch = async (
    url: string,
    method: HttpMethod,
    body?: FormData | string,
    headers: HeadersInit = { Accept: JSON_MIME_TYPE },
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const auth = authInit(
      this.currentTransport,
      () => this.session.sessionToken,
      "raw"
    )
    let requestHeaders: Record<string, string> = { ...auth.headers }
    const castleHeaders = await this.session.getCastleHeaders()
    if (!isEmpty(castleHeaders)) {
      requestHeaders = { ...requestHeaders, ...castleHeaders }
    }
    if (this.session.recoveryCode.length > 0) {
      requestHeaders[RECOVERY_CODE_HEADER] = this.session.recoveryCode
    } else {
      if (this.session.oneTimePassword.length > 0) {
        requestHeaders[OTP_HEADER] = this.session.oneTimePassword
      }
      if (this.session.twoFactorMethod.length > 0) {
        requestHeaders[TWO_FACTOR_METHOD_HEADER] = this.session.twoFactorMethod
      }
    }

    if (
      typeof body !== "string" &&
      !isNil(body) &&
      [...body.values()].some((v) => v instanceof File)
    ) {
      // Contains at least one file; ensure content-type header is
      // unset so user-agent sets it with boundary.
      // oxlint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete requestHeaders[CONTENT_TYPE]
    } else {
      // Defaults to application/json without files. Can be overriden by `headers` passed in
      requestHeaders[CONTENT_TYPE] = JSON_MIME_TYPE
    }

    Object.assign(requestHeaders, headers)

    const request: RequestInit = {
      body,

      headers: requestHeaders,
      method,
      signal: resolveSignal(options),
    }
    if (auth.credentials) {
      request.credentials = auth.credentials
    }

    const response = await this.fetchWithErrorHandler(url, request)

    if (isOTPResponse(response)) {
      const attemptsRemaining = response.headers.get(
        attemptsRemainingHeaderFor(this.session)
      )
      this.sessionErrorHandler.onOTPRequired(response)
      const helper = await toJsonHelper(response)

      throw new TwoFactorAuthError(
        method,
        url,
        response,
        helper,
        attemptsRemaining
      )
    } else if (
      response.status === HTTP_INVALID_SESSION &&
      this.session.recoveryCode.length > 0 &&
      response.headers.get(RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER) !== null
    ) {
      const baseResponse = new BaseResponse()
      parseErrorContainer(await toJsonHelper(response), baseResponse)

      throw new InvalidAnswerError(
        baseResponse.errorContainer.message,
        response.headers.get(RECOVERY_CODE_ATTEMPTS_REMAINING_HEADER)
      )
    } else if (response.status === HTTP_INVALID_SESSION) {
      const baseResponse = new BaseResponse()
      const helper = await toJsonHelper(response)
      parseErrorContainer(helper, baseResponse)
      this.sessionErrorHandler.onInvalidSession(baseResponse)

      if (baseResponse.errorContainer.code !== INVALID_CREDENTIALS_CODE) {
        throw new InvalidSessionError(method, url, response, helper)
      }
      return helper
    } else if (isTooManyRequestsResponse(response)) {
      throw new TooManyRequestsError(method, url, response)
    } else if (response.status >= HTTP_INTERNAL_SERVER_ERROR) {
      throw new InternalServerError(method, url, response)
    }

    return toJsonHelper(response)
  }

  get = async (
    path = "",
    params?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "GET", undefined, headers, options)
  }

  patch = async (
    path = "",
    body: any,
    params?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "PATCH", JSON.stringify(body), headers, options)
  }

  put = async (
    path = "",
    body: any,
    params?: Params,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "PUT", JSON.stringify(body), undefined, options)
  }

  postJson = async (
    path = "",
    body: any,
    pathParams?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParams)
    return this.fetch(url, "POST", JSON.stringify(body), headers, options)
  }

  postForm = async (
    path: string,
    formParams: Params,
    pathParam?: Params,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    return this.fetch(
      url,
      "POST",
      toFormData(formParams),
      {
        [CONTENT_TYPE]: MULTIPART_FORM_MIME_TYPE,
      },
      options
    )
  }

  postFiles = async (
    path: string,
    formParams: Params,
    fileParams: FileParams,
    pathParam?: Params,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    const formData = toFormData(formParams)
    for (const [key, value] of Object.entries(fileParams)) {
      formData.append(key, value, value.name)
    }
    return this.fetch(url, "POST", formData, {}, options)
  }

  doDelete = async (
    path = "",
    pathParam?: Params,
    headers?: HeadersInit,
    body?: any,
    options?: RequestOptions
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    return this.fetch(
      url,
      "DELETE",
      // oxlint-disable-next-line eqeqeq
      body == null ? undefined : JSON.stringify(body),
      headers,
      options
    )
  }

  index = async <K, V>(
    target: ArrayMap<K, V>,
    updater: Updater<V>,
    jsonKeyExtractor: JsonKeyExtractor<K>,
    path?: string,
    params?: Params,
    options?: RequestOptions
  ): Promise<ItemsResponse<V>> =>
    this.get(path, params, undefined, options).then((helper) =>
      updateItemsResponse(helper, target, updater, jsonKeyExtractor)
    )

  indexSimple = async <T>(
    parser: Parser<T>,
    path?: string,
    params?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<ItemsResponse<T>> =>
    this.get(path, params, headers, options).then((helper) =>
      parseItemsResponse(helper, parser)
    )

  show = async <T>(
    target: T,
    updater: Updater<T>,
    path?: string,
    params?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<SingleResponse<T>> =>
    this.get(path, params, headers, options).then((helper) =>
      updateSingleResponse(helper, target, updater)
    )

  create = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string,
    pathParams?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.postJson(path, builder.json, pathParams, headers, options).then(
      (helper) => updateSingleResponse(helper, target, updater)
    )
  }

  update = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string,
    pathParams?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.patch(path, builder.json, pathParams, headers, options).then(
      (helper) => updateSingleResponse(helper, target, updater)
    )
  }

  updateResource = async <T extends RestResource>(
    target: T,
    deSer: DeSer<T>
  ): Promise<SingleResponse<T>> =>
    actionOnSingleResource(this.patch, target, deSer)

  save = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string,
    options?: RequestOptions
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.put(path, builder.json, undefined, options).then((helper) =>
      updateSingleResponse(helper, target, updater)
    )
  }

  saveResource = async <T extends RestResource>(
    target: T,
    deSer: DeSer<T>
  ): Promise<SingleResponse<T>> =>
    actionOnSingleResource(this.put, target, deSer)

  destroy = async (
    path?: string,
    pathParams?: Params,
    headers?: HeadersInit,
    options?: RequestOptions
  ): Promise<BaseResponse> => {
    const helper = await this.doDelete(
      path,
      pathParams,
      headers,
      undefined,
      options
    )

    const response = new BaseResponse()
    parseErrorContainer(helper, response)

    return response
  }
}

export type AuthScheme = "raw" | "bearer"

export interface AuthRequestInit {
  headers: Record<string, string>
  credentials?: RequestCredentials
}

export interface AuthSource {
  readonly currentTransport: Transport
  readonly twSession: { readonly sessionToken: string }
}

export type AuthFetchRequestInit = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>
}

function authInit(
  transport: Transport,
  readSessionToken: () => string,
  scheme: AuthScheme
): AuthRequestInit {
  if (transport === "cookie-csrf") {
    return {
      headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
      credentials: "include",
    }
  }

  const sessionToken = readSessionToken()

  if (isEmpty(sessionToken)) {
    return { headers: {} }
  }

  return {
    headers: {
      [AUTHORIZATION]:
        scheme === "bearer" ? `Bearer ${sessionToken}` : sessionToken,
    },
  }
}

function withoutAuthorization(
  headers: Record<string, string>
): Record<string, string> {
  const authorization = AUTHORIZATION.toLowerCase()

  return Object.fromEntries(
    Object.entries(headers).filter(
      ([name]) => name.toLowerCase() !== authorization
    )
  )
}

export function authRequestInit(
  client: HttpClient,
  sessionToken?: string
): AuthRequestInit {
  return authInit(
    client.currentTransport,
    () => sessionToken ?? client.session.sessionToken,
    "raw"
  )
}

export function authFetchInit(
  source: AuthSource,
  init: AuthFetchRequestInit = {},
  { scheme = "raw" }: { scheme?: AuthScheme } = {}
): RequestInit {
  const auth = authInit(
    source.currentTransport,
    () => source.twSession.sessionToken,
    scheme
  )

  if (auth.credentials) {
    return {
      ...init,
      headers: { ...withoutAuthorization(init.headers ?? {}), ...auth.headers },
      credentials: auth.credentials,
    }
  }

  return { ...init, headers: { ...init.headers, ...auth.headers } }
}
